/**
 * Tabs in article Markdown, as the Rise import writes a Rise tabs block:
 *
 *   :::tabs
 *   #### Über den Schreibtisch
 *   Text and pictures of the first tab ...
 *   #### Über den Aktenplan
 *   Text and pictures of the second tab ...
 *   :::
 *
 * Each heading at the level of the block's first heading starts a tab and
 * names it (components/ArticleTabs.js); anything before that first heading is
 * shown above the tabs. "reiter" works as well as "tabs". Other ":::" blocks
 * (a ":::vergleich" inside a tab) may be nested. A block without a heading is
 * shown as ordinary text; fenced code blocks are left alone.
 */

const OPEN = /^\s*:::\s*(tabs|reiter)\s*$/i;
const NESTED_OPEN = /^\s*:::\s*\S/;
const CLOSE = /^\s*:::\s*$/;
const FENCE = /^\s*(```|~~~)/;
const HEADING = /^\s*(#{2,6})\s+(.+?)\s*#*\s*$/;

/** The tabs of a block's body, or null when it has none. */
function parseTabs(lines) {
  const first = lines.find((line) => HEADING.test(line));
  if (!first) return null;

  const level = first.match(HEADING)[1].length;
  const intro = [];
  const tabs = [];

  for (const line of lines) {
    const match = line.match(HEADING);
    if (match && match[1].length === level) {
      tabs.push({ title: match[2], lines: [] });
    } else if (tabs.length) {
      tabs[tabs.length - 1].lines.push(line);
    } else {
      intro.push(line);
    }
  }

  return {
    intro: intro.join('\n').trim(),
    tabs: tabs.map((tab) => ({ title: tab.title, text: tab.lines.join('\n').trim() })),
  };
}

/** Splits Markdown into { type: 'markdown', text } and { type: 'tabs', intro, tabs: [{ title, text }] }. */
export function splitTabBlocks(markdown = '') {
  const lines = markdown.split(/\r?\n/);
  const segments = [];
  let buffer = [];
  let block = null;
  let depth = 0;
  let inFence = false;

  function flushText() {
    if (buffer.length) {
      segments.push({ type: 'markdown', text: buffer.join('\n') });
      buffer = [];
    }
  }

  for (const line of lines) {
    if (FENCE.test(line)) {
      inFence = !inFence;
    }

    if (!inFence && !block && OPEN.test(line)) {
      block = [];
      depth = 0;
      continue;
    }

    if (block && !inFence) {
      if (CLOSE.test(line)) {
        if (depth === 0) {
          const parsed = parseTabs(block);
          if (parsed) {
            flushText();
            segments.push({ type: 'tabs', ...parsed });
          } else {
            buffer.push(...block);
          }
          block = null;
          continue;
        }
        depth -= 1;
      } else if (NESTED_OPEN.test(line)) {
        depth += 1;
      }
    }

    (block ?? buffer).push(line);
  }

  // An unclosed block is just text.
  if (block) {
    buffer.push(...block);
  }

  flushText();
  return segments;
}

/**
 * The Markdown without its tab blocks - for the table of contents, which
 * would otherwise list headings that sit in a hidden tab.
 */
export function markdownWithoutTabs(markdown = '') {
  return splitTabBlocks(markdown)
    .map((segment) => (segment.type === 'markdown' ? segment.text : segment.intro))
    .join('\n');
}
